import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { ArrowRight, Star, ChevronDown, ChevronUp, Cpu, Lock, Globe, Zap } from "lucide-react";
import { useBrand } from "@/config/brands";
import { Helmet } from "react-helmet-async";

const RED = "#B22234";
const NAVY = "#002868";
const GOLD = "#C5A55A";
const CREAM = "#FAF9F6";
const GREEN = "#1a7a3a";

const MCP_TOOLS = [
  {
    name: "search_hospitals",
    access: "PUBLIC",
    accessColor: GREEN,
    desc: "Search for US hospitals by name and/or state. Returns location, grade, markup score, and profile link.",
    params: [
      { name: "name", type: "string", required: false, desc: 'Hospital name, partial match OK (e.g., "Mayo", "Memorial")' },
      { name: "state", type: "string", required: false, desc: 'Two-letter state code (e.g., "TX", "CA")' },
      { name: "limit", type: "integer", required: false, desc: "Max results to return (default 10, max 50)" },
    ],
    example: `search_hospitals(name="Mayo", state="MN", limit=5)`,
    response: `{
  "hospitals": [
    {
      "id": "uuid...",
      "name": "Mayo Clinic Hospital - Rochester",
      "city": "Rochester", "state": "MN",
      "grade": "A",
      "markup_score": 12.4,
      "avg_gross_markup_pct": -8.2,
      "profile_url": "https://americanhospitalprices.com/hospital/uuid..."
    }
  ],
  "count": 1
}`,
  },
  {
    name: "get_hospital_rankings",
    access: "PUBLIC",
    accessColor: GREEN,
    desc: "Get hospitals ranked by markup score — highest average markup first. Filter by state or letter grade.",
    params: [
      { name: "state", type: "string", required: false, desc: 'Filter by state (e.g., "TX")' },
      { name: "grade_filter", type: "string", required: false, desc: 'Filter by grade: "A", "B", "C", "D", or "F"' },
      { name: "limit", type: "integer", required: false, desc: "Max results (default 20, max 100)" },
    ],
    example: `get_hospital_rankings(state="TX", grade_filter="F", limit=10)`,
    response: `{
  "rankings": [
    {
      "national_rank": 3,
      "name": "Some Hospital",
      "city": "Houston", "state": "TX",
      "grade": "F",
      "markup_score": 94.7,
      "avg_gross_markup_pct": 312.5
    }
  ],
  "count": 10
}`,
  },
  {
    name: "get_hospital_detail",
    access: "PUBLIC",
    accessColor: GREEN,
    desc: "Get full details for one hospital — grade, markup, CMS quality ratings, bed count, ownership.",
    params: [
      { name: "hospital_id", type: "string", required: true, desc: "Hospital UUID from search_hospitals" },
    ],
    example: `get_hospital_detail(hospital_id="3f2a8c19-...")`,
    response: `{
  "name": "Mayo Clinic Hospital",
  "grade": "A",
  "markup_score": 12.4,
  "avg_gross_markup_pct": -8.2,
  "bed_count": 1265,
  "hospital_type": "General Acute Care",
  "cms_quality": {
    "overall_rating": 5,
    "safety": "Above the National Rate"
  }
}`,
  },
  {
    name: "get_procedure_pricing",
    access: "PUBLIC",
    accessColor: GREEN,
    desc: "Get national pricing statistics for any CPT procedure code — median, average, min, max across all reporting hospitals.",
    params: [
      { name: "cpt_code", type: "string", required: true, desc: 'CPT code (e.g., "99213", "27447", "93000")' },
    ],
    example: `get_procedure_pricing(cpt_code="27447")`,
    response: `{
  "cpt_code": "27447",
  "name": "Total Knee Replacement",
  "national_pricing": {
    "median_charge": 47250.00,
    "avg_charge": 52100.00,
    "min_charge": 8400.00,
    "max_charge": 198000.00,
    "hospitals_reporting": 1842
  }
}`,
  },
  {
    name: "search_procedures",
    access: "PUBLIC",
    accessColor: GREEN,
    desc: 'Search for procedures by plain-English name to discover CPT codes and national pricing. "What CPT code is a hip replacement?"',
    params: [
      { name: "query", type: "string", required: true, desc: 'Search term (e.g., "hip replacement", "MRI brain", "colonoscopy")' },
      { name: "limit", type: "integer", required: false, desc: "Max results (default 10, max 30)" },
    ],
    example: `search_procedures(query="knee replacement", limit=5)`,
    response: `{
  "procedures": [
    {
      "cpt_code": "27447",
      "name": "Total Knee Replacement",
      "median_national_charge": 47250.00,
      "hospitals_reporting": 1842
    }
  ],
  "count": 1
}`,
  },
  {
    name: "get_scenario_costs",
    access: "PUBLIC",
    accessColor: GREEN,
    desc: "Get full cost breakdown for a pre-built medical scenario — an episode of care with all CPT codes, phases, and national median costs bundled.",
    params: [
      { name: "scenario_id", type: "string", required: true, desc: "Scenario UUID (e.g., from the Scenarios page)" },
    ],
    example: `get_scenario_costs(scenario_id="abc123...")`,
    response: `{
  "name": "Total Knee Replacement Surgery",
  "specialty": "orthopedics",
  "cost_estimate": {
    "total_cost": 52400.00,
    "min_cost": 18200.00,
    "max_cost": 210000.00,
    "procedures_with_pricing": 12
  },
  "phase_costs": {...}
}`,
  },
  {
    name: "compare_hospitals",
    access: "PARTNER",
    accessColor: GOLD,
    desc: "Compare 2–10 hospitals side by side. Optionally drill into a specific CPT procedure to see each hospital's price vs the national median.",
    params: [
      { name: "hospital_ids", type: "string[]", required: true, desc: "List of 2–10 hospital UUIDs" },
      { name: "cpt_code", type: "string", required: false, desc: "Optional CPT code for procedure-level comparison" },
    ],
    example: `compare_hospitals(
  hospital_ids=["uuid1", "uuid2", "uuid3"],
  cpt_code="27447"
)`,
    response: `{
  "cheapest_overall": "Mayo Clinic Hospital",
  "most_expensive_overall": "Some For-Profit Hospital",
  "comparison": [
    { "name": "Mayo Clinic", "grade": "A", "avg_markup_pct": -8.2,
      "procedure_pricing": { "median_price": 32000, "pct_above_national_median": -32.3 }},
    ...
  ]
}`,
  },
  {
    name: "get_state_summary",
    access: "PUBLIC",
    accessColor: GREEN,
    desc: "Get an aggregate overview of hospital pricing fairness for an entire state — grade distribution, average markup, and markup score.",
    params: [
      { name: "state", type: "string", required: true, desc: 'Two-letter state code (e.g., "TX", "CA", "NY")' },
    ],
    example: `get_state_summary(state="TX")`,
    response: `{
  "state": "TX",
  "total_hospitals": 612,
  "hospitals_with_pricing_data": 418,
  "avg_markup_pct": 87.4,
  "median_markup_pct": 61.2,
  "grade_distribution": {
    "A": 42, "B": 71, "C": 103, "D": 89, "F": 113, "no_data": 194
  }
}`,
  },
];

const DESKTOP_CONFIG = `{
  "mcpServers": {
    "ahp-medical-prices": {
      "type": "http",
      "url": "https://mcp.americanhospitalprices.com/mcp"
    }
  }
}`;

const CLAUDE_CODE_CMD = `claude mcp add --transport http ahp https://mcp.americanhospitalprices.com/mcp`;

const STATS = [
  { value: "9,500+", label: "Hospitals" },
  { value: "546M+", label: "Procedure prices" },
  { value: "50", label: "States" },
  { value: "Free", label: "No key required" },
];

export default function MCPAccess() {
  const { toast } = useToast();
  const brand = useBrand();
  const [expandedTool, setExpandedTool] = useState<string | null>(null);
  const [showMcpExplainer, setShowMcpExplainer] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    company_name: "",
    job_title: "",
    use_case_description: "",
    ai_application_type: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const { error } = await supabase.from("api_access_requests").insert({
        full_name: formData.full_name,
        email: formData.email,
        company_name: formData.company_name || null,
        job_title: formData.job_title || null,
        use_case: "mcp_partner",
        use_case_description: `[MCP Partner Request] AI App: ${formData.ai_application_type}\n\n${formData.use_case_description}`,
        status: "pending",
        interested_endpoints: ["mcp"],
      });
      if (error) throw error;
      toast({
        title: "Application Submitted!",
        description: "We'll review your MCP partner request within 48 hours.",
        duration: 5000,
      });
      setShowForm(false);
      setFormData({ full_name: "", email: "", company_name: "", job_title: "", use_case_description: "", ai_application_type: "" });
    } catch {
      toast({
        title: "Submission Failed",
        description: "There was an error submitting your request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ background: CREAM, minHeight: "100vh", paddingBottom: "7rem", fontFamily: "'Instrument Serif', Georgia, serif" }}>
      <Helmet>
        <title>MCP Access — AI Agent Medical Price Data | {brand.name}</title>
        <meta name="description" content="The first public MCP endpoint for national hospital price transparency data. Give your AI agent real-time access to 546M+ procedure prices across 9,500+ US hospitals." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${brand.primaryDomain}/mcp`} />
        <meta property="og:title" content="MCP Access — AI Agent Medical Price Data" />
        <meta property="og:description" content="The first public MCP endpoint for national hospital price transparency data." />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <div style={{
        background: `linear-gradient(175deg, ${NAVY} 0%, #001a4d 60%, #0a1f5c 100%)`,
        position: "relative", overflow: "hidden",
        paddingTop: "3.5rem", paddingBottom: "3rem",
      }}>
        {/* Flag stripe brackets the hero against the global header (matches the app). */}
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${RED} 0%, ${CREAM} 50%, ${NAVY} 100%)` }} />

        <div style={{ maxWidth: 760, margin: "0 auto", padding: "0 1.5rem", textAlign: "center", position: "relative", zIndex: 1 }}>
          <div style={{ display: "inline-block", letterSpacing: "0.22em", fontSize: "0.7rem", color: GOLD, textTransform: "uppercase", marginBottom: "0.9rem", fontFamily: "'Instrument Sans', system-ui, sans-serif", fontWeight: 700 }}>
            AI Agent Access
          </div>

          {/* Launch badge */}
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "1rem" }}>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: `${GOLD}22`, border: `1px solid ${GOLD}66`,
              padding: "0.3rem 0.85rem", fontSize: "0.7rem", fontFamily: "'Instrument Sans', system-ui, sans-serif",
              color: GOLD, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 700,
            }}>
              <Zap size={11} color={GOLD} />
              First Public Medical MCP
            </div>
          </div>

          <h1 style={{ fontSize: "clamp(2.1rem, 6vw, 3.4rem)", fontWeight: 400, lineHeight: 1.05, color: "white", marginBottom: "0.6rem", letterSpacing: "-0.01em", fontFamily: "'Instrument Serif', Georgia, serif" }}>
            Medical Price Data <span style={{ color: GOLD }}>for AI Agents</span>
          </h1>

          <p style={{ color: "rgba(255,255,255,0.78)", fontSize: "1.05rem", lineHeight: 1.65, maxWidth: 560, margin: "0 auto 2rem", fontFamily: "'Instrument Sans', system-ui, sans-serif", fontWeight: 400 }}>
            The first public MCP endpoint for national hospital price transparency data.
            Give your AI agent real-time access to 546M+ procedure prices across 9,500+ US hospitals.
          </p>

          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button
              onClick={() => { document.getElementById("setup-section")?.scrollIntoView({ behavior: "smooth" }); }}
              style={{
                background: RED, color: "white", border: "none", cursor: "pointer",
                padding: "0.85rem 1.75rem", fontSize: "0.9rem", fontWeight: 700,
                letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: "'Instrument Sans', system-ui, sans-serif",
                display: "inline-flex", alignItems: "center", gap: 8,
                boxShadow: "0 4px 20px rgba(178,34,52,0.4)",
              }}
            >
              Connect in Claude Desktop <ArrowRight size={16} />
            </button>
            <button
              onClick={() => { setShowForm(true); setTimeout(() => document.getElementById("partner-form")?.scrollIntoView({ behavior: "smooth" }), 50); }}
              style={{
                background: "transparent", color: GOLD, border: `2px solid ${GOLD}88`, cursor: "pointer",
                padding: "0.85rem 1.75rem", fontSize: "0.9rem", fontWeight: 700,
                letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: "'Instrument Sans', system-ui, sans-serif",
                display: "inline-flex", alignItems: "center", gap: 8,
              }}
            >
              Apply for Partner Access
            </button>
          </div>
        </div>

        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 4, background: RED }} />
      </div>

      {/* ── STATS BAR ─────────────────────────────────────────────── */}
      <div style={{ background: "white", borderBottom: `1px solid #e8e3d8`, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
        <div style={{ maxWidth: 760, margin: "0 auto", padding: "0 1.5rem", display: "flex", justifyContent: "space-around" }}>
          {STATS.map(stat => (
            <div key={stat.label} style={{ textAlign: "center", padding: "1.1rem 0" }}>
              <div style={{ fontSize: "1.6rem", fontWeight: 800, color: NAVY, fontFamily: "'Instrument Serif', Georgia, serif", lineHeight: 1 }}>{stat.value}</div>
              <div style={{ fontSize: "0.7rem", color: "#888", textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "'Instrument Sans', system-ui, sans-serif", marginTop: 3 }}>{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── MAIN CONTENT ────────────────────────────────────────────── */}
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "2.5rem 1.5rem 0" }}>

        {/* ── WHAT IS MCP? ─────────────────────────────────────────── */}
        <SectionHeader icon={<Cpu size={16} color={NAVY} />} title="What is MCP?" />

        <div style={{
          background: "white", border: `1px solid #ddd8cc`, borderRadius: 12,
          boxShadow: "0 2px 16px rgba(0,0,0,0.06)", marginBottom: "2rem", overflow: "hidden",
        }}>
          <button
            onClick={() => setShowMcpExplainer(!showMcpExplainer)}
            style={{
              width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "1rem 1.25rem", background: "none", border: "none", cursor: "pointer", textAlign: "left",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ background: NAVY, color: "white", fontFamily: "monospace", fontSize: "0.7rem", fontWeight: 700, padding: "0.2rem 0.5rem", letterSpacing: "0.05em" }}>
                MCP 2025
              </div>
              <span style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif", fontSize: "0.875rem", color: "#444" }}>
                Model Context Protocol · Streamable HTTP · AI-native data access
              </span>
            </div>
            {showMcpExplainer ? <ChevronUp size={16} color="#999" /> : <ChevronDown size={16} color="#999" />}
          </button>

          {showMcpExplainer && (
            <div style={{ padding: "0 1.25rem 1.25rem", borderTop: `1px solid #f0ebe0` }}>
              <p style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif", fontSize: "0.875rem", color: "#555", lineHeight: 1.7, margin: "0.85rem 0 0.75rem" }}>
                The <strong>Model Context Protocol (MCP)</strong> is an open standard that lets AI assistants like Claude
                connect to live data sources. Instead of relying on training data that may be months old, an AI agent
                with an MCP connection can query real databases in real time — and return structured, up-to-date answers.
              </p>
              <p style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif", fontSize: "0.875rem", color: "#555", lineHeight: 1.7, margin: "0 0 0.5rem" }}>
                For healthcare AI, this matters enormously. Hospital prices change. New data gets published.
                By connecting Claude to <code style={{ background: "#f0ebe0", padding: "0.1rem 0.35rem", fontFamily: "monospace", fontSize: "0.8rem" }}>mcp.americanhospitalprices.com</code>,
                you can ask questions like <em>"Which hospital in Dallas has the lowest price for a knee replacement?"</em> and
                get a live, data-backed answer — not a guess.
              </p>
            </div>
          )}
        </div>

        {/* ── MCP TOOLS ─────────────────────────────────────────────── */}
        <SectionHeader icon={<Globe size={16} color={NAVY} />} title="MCP Tools" />
        <p style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif", fontSize: "0.875rem", color: "#777", marginBottom: "1.5rem", lineHeight: 1.6 }}>
          8 tools available. Public tools are open to all Claude Desktop users. Partner tools require an API token.
        </p>

        <div style={{ marginBottom: "2.5rem" }}>
          {MCP_TOOLS.map((tool) => {
            const isOpen = expandedTool === tool.name;
            return (
              <div key={tool.name} style={{
                background: "white", border: `1px solid #ddd8cc`, borderRadius: 12, marginBottom: 8,
                boxShadow: isOpen ? "0 2px 12px rgba(0,0,0,0.08)" : "0 1px 4px rgba(0,0,0,0.04)",
                overflow: "hidden",
              }}>
                <button
                  onClick={() => setExpandedTool(isOpen ? null : tool.name)}
                  style={{
                    width: "100%", display: "flex", alignItems: "center", gap: 12,
                    padding: "1rem 1.25rem", background: "none", border: "none",
                    cursor: "pointer", textAlign: "left",
                  }}
                >
                  <div style={{
                    background: tool.accessColor, color: "white", fontFamily: "'Instrument Sans', system-ui, sans-serif",
                    fontWeight: 700, fontSize: "0.65rem", padding: "0.2rem 0.5rem",
                    letterSpacing: "0.08em", flexShrink: 0, minWidth: 58, textAlign: "center",
                  }}>
                    {tool.access}
                  </div>
                  <div style={{ fontFamily: "monospace", fontSize: "0.9rem", color: NAVY, fontWeight: 600, flex: 1 }}>
                    {tool.name}
                  </div>
                  {isOpen ? <ChevronUp size={16} color="#999" /> : <ChevronDown size={16} color="#999" />}
                </button>

                {isOpen && (
                  <div style={{ padding: "0 1.25rem 1.25rem", borderTop: `1px solid #f0ebe0` }}>
                    <p style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif", fontSize: "0.875rem", color: "#444", margin: "0.85rem 0 1rem", lineHeight: 1.65 }}>
                      {tool.desc}
                    </p>

                    {/* Params table */}
                    <div style={{ marginBottom: "1rem", overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "'Instrument Sans', system-ui, sans-serif", fontSize: "0.78rem" }}>
                        <thead>
                          <tr style={{ background: "#f8f5ee" }}>
                            <th style={{ padding: "0.4rem 0.6rem", textAlign: "left", color: "#666", fontWeight: 600, borderBottom: `1px solid #e5dfc8` }}>Parameter</th>
                            <th style={{ padding: "0.4rem 0.6rem", textAlign: "left", color: "#666", fontWeight: 600, borderBottom: `1px solid #e5dfc8` }}>Type</th>
                            <th style={{ padding: "0.4rem 0.6rem", textAlign: "left", color: "#666", fontWeight: 600, borderBottom: `1px solid #e5dfc8` }}>Required</th>
                            <th style={{ padding: "0.4rem 0.6rem", textAlign: "left", color: "#666", fontWeight: 600, borderBottom: `1px solid #e5dfc8` }}>Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tool.params.map(p => (
                            <tr key={p.name} style={{ borderBottom: `1px solid #f0ebe0` }}>
                              <td style={{ padding: "0.4rem 0.6rem", fontFamily: "monospace", color: NAVY, fontWeight: 600 }}>{p.name}</td>
                              <td style={{ padding: "0.4rem 0.6rem", color: "#888", fontFamily: "monospace" }}>{p.type}</td>
                              <td style={{ padding: "0.4rem 0.6rem", color: p.required ? RED : "#aaa" }}>{p.required ? "yes" : "no"}</td>
                              <td style={{ padding: "0.4rem 0.6rem", color: "#555" }}>{p.desc}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Example call */}
                    <div style={{ marginBottom: "0.75rem" }}>
                      <div style={{ fontSize: "0.72rem", fontFamily: "'Instrument Sans', system-ui, sans-serif", color: "#999", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Example Call</div>
                      <div style={{
                        background: "#f8f5ee", border: `1px solid #e5dfc8`, borderRadius: 12, borderLeft: `3px solid ${NAVY}`,
                        padding: "0.65rem 0.85rem", fontFamily: "monospace", fontSize: "0.8rem", color: NAVY,
                        whiteSpace: "pre",
                      }}>
                        {tool.example}
                      </div>
                    </div>

                    {/* Example response */}
                    <div>
                      <div style={{ fontSize: "0.72rem", fontFamily: "'Instrument Sans', system-ui, sans-serif", color: "#999", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Example Response</div>
                      <div style={{
                        background: "#0f1729", border: `1px solid #1e2d4d`, borderRadius: 12, borderLeft: `3px solid ${GOLD}`,
                        padding: "0.65rem 0.85rem", fontFamily: "monospace", fontSize: "0.78rem", color: "#a8c4e0",
                        whiteSpace: "pre", overflowX: "auto",
                      }}>
                        {tool.response}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── SETUP: CLAUDE DESKTOP ────────────────────────────────── */}
        <div id="setup-section">
          <SectionHeader icon={<Cpu size={16} color={NAVY} />} title="Connect in Claude (web, mobile, desktop)" />
        </div>

        <div style={{ background: "white", border: `1px solid #ddd8cc`, borderRadius: 12, boxShadow: "0 2px 16px rgba(0,0,0,0.06)", marginBottom: "2.5rem", overflow: "hidden" }}>
          {[
            { step: "1", title: "Open Claude settings", detail: "claude.ai (web or mobile): Settings → Connectors. Claude Desktop: Claude menu → Settings → Connectors." },
            { step: "2", title: 'Click "Add custom connector"', detail: "No API key and no account with us needed — the server is public and read-only." },
            { step: "3", title: "Enter the server URL", detail: "URL: https://mcp.americanhospitalprices.com/mcp" },
            { step: "4", title: "Ask about any hospital price", detail: '"What does a knee replacement cost near Houston, and which hospital is cheapest?"' },
          ].map((s, i, arr) => (
            <div key={s.step} style={{ display: "flex", gap: 16, padding: "1.1rem 1.25rem", borderTop: i > 0 ? `1px solid #f0ebe0` : "none", alignItems: "flex-start" }}>
              <div style={{
                width: 28, height: 28, borderRadius: "50%", background: NAVY, color: "white",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: "'Instrument Sans', system-ui, sans-serif", fontWeight: 800, fontSize: "0.8rem", flexShrink: 0,
              }}>
                {s.step}
              </div>
              <div>
                <div style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif", fontWeight: 700, fontSize: "0.875rem", color: "#222", marginBottom: 2 }}>{s.title}</div>
                <div style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif", fontSize: "0.825rem", color: "#777" }}>{s.detail}</div>
              </div>
            </div>
          ))}

          <div style={{ padding: "0 1.25rem 1.25rem" }}>
            <div style={{ fontSize: "0.72rem", fontFamily: "'Instrument Sans', system-ui, sans-serif", color: "#999", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
              Alternative: claude_desktop_config.json
            </div>
            <div style={{
              background: "#0f1729", border: `1px solid #1e2d4d`, borderRadius: 12, borderLeft: `3px solid ${GOLD}`,
              padding: "0.75rem 1rem", fontFamily: "monospace", fontSize: "0.78rem", color: "#a8c4e0",
              whiteSpace: "pre", overflowX: "auto",
            }}>
              {DESKTOP_CONFIG}
            </div>
          </div>
        </div>

        {/* ── LIVE + PUBLIC ─────────────────────────────────────────── */}
        <div style={{
          background: `${GREEN}10`, border: `1px solid ${GREEN}33`,
          borderLeft: `4px solid ${GREEN}`, padding: "1.25rem 1.5rem", marginBottom: "2.5rem",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: "0.5rem" }}>
            <Zap size={14} color={GREEN} />
            <span style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif", fontWeight: 700, fontSize: "0.8rem", color: GREEN, textTransform: "uppercase", letterSpacing: "0.08em" }}>
              Live: fully public endpoint
            </span>
          </div>
          <p style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif", fontSize: "0.85rem", color: "#555", lineHeight: 1.65, margin: 0 }}>
            {"The server is public, read-only, and needs no token \u2014 your AI subscription does the reasoning, we serve the data. Rate limited at 120 requests/minute per IP; partner tokens (below) unlock the compare_hospitals bulk tool and higher limits."}
          </p>
        </div>

        {/* ── SETUP: CLAUDE CODE ────────────────────────────────────── */}
        <SectionHeader icon={<Cpu size={16} color={NAVY} />} title="Connect in Claude Code" />
        <div style={{ background: "white", border: `1px solid #ddd8cc`, borderRadius: 12, boxShadow: "0 2px 16px rgba(0,0,0,0.06)", marginBottom: "2.5rem", padding: "1.1rem 1.25rem" }}>
          <p style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif", fontSize: "0.85rem", color: "#555", margin: "0 0 0.6rem" }}>
            One command in any terminal:
          </p>
          <div style={{
            background: "#0f1729", border: `1px solid #1e2d4d`, borderRadius: 12, borderLeft: `3px solid ${GOLD}`,
            padding: "0.75rem 1rem", fontFamily: "monospace", fontSize: "0.78rem", color: "#a8c4e0",
            whiteSpace: "pre", overflowX: "auto",
          }}>
            {CLAUDE_CODE_CMD}
          </div>
        </div>

        {/* ── AGENT SKILL ───────────────────────────────────────────── */}
        <SectionHeader icon={<Zap size={16} color={NAVY} />} title="Optional: the AHP price-analyst skill" />
        <div style={{ background: "white", border: `1px solid #ddd8cc`, borderRadius: 12, boxShadow: "0 2px 16px rgba(0,0,0,0.06)", marginBottom: "2.5rem", padding: "1.25rem" }}>
          <p style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif", fontSize: "0.875rem", color: "#555", lineHeight: 1.65, margin: "0 0 0.9rem" }}>
            {"The connector gives Claude the data; this skill teaches it the craft \u2014 median-first analysis, the exact grade scale, bill-audit and negotiation-comp workflows, and honest sourcing (every claim links back to the hospital's page). Upload it in Claude \u2192 Settings \u2192 Capabilities \u2192 Skills."}
          </p>
          <a
            href="/downloads/ahp-hospital-prices-skill.zip"
            download
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: NAVY, color: "white", padding: "0.6rem 1.1rem", borderRadius: 8,
              fontFamily: "'Instrument Sans', system-ui, sans-serif", fontWeight: 700, fontSize: "0.85rem",
              textDecoration: "none",
            }}
          >
            {"Download ahp-hospital-prices-skill.zip (2 KB)"}
          </a>
        </div>

        {/* ── SETUP: CHATGPT ────────────────────────────────────────── */}
        <SectionHeader icon={<Cpu size={16} color={NAVY} />} title="Connect in ChatGPT" />
        <div style={{ background: "white", border: `1px solid #ddd8cc`, borderRadius: 12, boxShadow: "0 2px 16px rgba(0,0,0,0.06)", marginBottom: "2.5rem", overflow: "hidden" }}>
          {[
            { step: "1", title: "Open ChatGPT connector settings", detail: "Settings \u2192 Connectors \u2192 Advanced \u2192 Develop \u2192 Add custom connector (requires a ChatGPT plan with connectors/developer mode)." },
            { step: "2", title: "Add the MCP server", detail: "URL: https://mcp.americanhospitalprices.com/mcp \u2014 no authentication." },
            { step: "3", title: "Prefer a no-setup path?", detail: "Any custom GPT built on our REST API works too: the OpenAPI spec lives at americanhospitalprices.com/openapi.json \u2014 point a GPT Action at it." },
          ].map((s, i) => (
            <div key={s.step} style={{ display: "flex", gap: 16, padding: "1.1rem 1.25rem", borderTop: i > 0 ? `1px solid #f0ebe0` : "none", alignItems: "flex-start" }}>
              <div style={{
                width: 28, height: 28, borderRadius: "50%", background: NAVY, color: "white",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: "'Instrument Sans', system-ui, sans-serif", fontWeight: 800, fontSize: "0.8rem", flexShrink: 0,
              }}>
                {s.step}
              </div>
              <div>
                <div style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif", fontWeight: 700, fontSize: "0.875rem", color: "#222", marginBottom: 2 }}>{s.title}</div>
                <div style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif", fontSize: "0.825rem", color: "#777" }}>{s.detail}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ── PARTNER ACCESS FORM ───────────────────────────────────── */}
        <SectionHeader icon={<Lock size={16} color={NAVY} />} title="Partner Access" />

        {!showForm ? (
          <div id="partner-form" style={{
            background: `linear-gradient(160deg, ${NAVY} 0%, #001a4d 100%)`,
            padding: "2.5rem 2rem", textAlign: "center", borderRadius: 14,
            position: "relative", overflow: "hidden",
          }}>
            <div style={{ position: "relative", zIndex: 1 }}>
              <h2 style={{ fontSize: "2rem", fontWeight: 400, color: "white", marginBottom: "0.5rem", fontFamily: "'Instrument Serif', Georgia, serif" }}>
                Apply for Partner Access
              </h2>
              <p style={{ color: "rgba(255,255,255,0.7)", marginBottom: "1.75rem", fontFamily: "'Instrument Sans', system-ui, sans-serif", fontSize: "0.95rem", maxWidth: 480, margin: "0 auto 1.75rem" }}>
                Get a bearer token for the <code style={{ fontFamily: "monospace", background: "rgba(255,255,255,0.1)", padding: "0.1rem 0.35rem" }}>compare_hospitals</code> partner tool
                and higher rate limits. Free public API for journalists, researchers, advocates, and apps.
              </p>
              <button
                onClick={() => setShowForm(true)}
                style={{
                  background: RED, color: "white", border: "none", cursor: "pointer",
                  padding: "0.9rem 2.25rem", fontSize: "0.95rem", fontWeight: 700,
                  letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: "'Instrument Sans', system-ui, sans-serif",
                  display: "inline-flex", alignItems: "center", gap: 8,
                  boxShadow: "0 4px 20px rgba(178,34,52,0.45)",
                }}
              >
                Apply for Partner Access <ArrowRight size={16} />
              </button>
            </div>
          </div>
        ) : (
          <div id="partner-form" style={{ background: "white", border: `1px solid #ddd8cc`, borderRadius: 12, boxShadow: "0 2px 20px rgba(0,0,0,0.08)", overflow: "hidden" }}>
            <div style={{ background: NAVY, padding: "1.5rem 1.75rem", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <h2 style={{ color: "white", fontSize: "1.25rem", fontWeight: 700, fontFamily: "'Instrument Serif', Georgia, serif", margin: 0 }}>
                  MCP Partner Access Application
                </h2>
                <p style={{ color: "rgba(255,255,255,0.65)", fontSize: "0.825rem", fontFamily: "'Instrument Sans', system-ui, sans-serif", margin: "0.25rem 0 0" }}>
                  Tell us about your AI application — we'll review within 48 hours
                </p>
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                {[...Array(3)].map((_, i) => <Star key={i} size={10} fill={GOLD} stroke="none" />)}
              </div>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: "1.75rem" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
                <FormField label="Full Name *" id="full_name" required
                  value={formData.full_name}
                  onChange={e => setFormData({ ...formData, full_name: e.target.value })} />
                <FormField label="Email Address *" id="email" type="email" required
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
                <FormField label="Company / Organization" id="company_name"
                  value={formData.company_name}
                  onChange={e => setFormData({ ...formData, company_name: e.target.value })} />
                <FormField label="Job Title / Role" id="job_title"
                  value={formData.job_title}
                  onChange={e => setFormData({ ...formData, job_title: e.target.value })} />
              </div>

              <div style={{ marginBottom: "1rem" }}>
                <Label htmlFor="ai_type" style={{ display: "block", marginBottom: 6, fontSize: "0.8rem", fontWeight: 600, color: "#444", textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "'Instrument Sans', system-ui, sans-serif" }}>
                  Type of AI Application *
                </Label>
                <Select required value={formData.ai_application_type} onValueChange={v => setFormData({ ...formData, ai_application_type: v })}>
                  <SelectTrigger style={{ border: `1px solid #ddd`, borderRadius: 0, height: 40 }}>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {[
                      ["claude_desktop", "Claude Desktop / Claude.ai"],
                      ["custom_agent", "Custom AI Agent"],
                      ["healthcare_app", "Healthcare Application"],
                      ["research_tool", "Research Tool"],
                      ["consumer_app", "Consumer App"],
                      ["enterprise", "Enterprise Software"],
                      ["other", "Other"],
                    ].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div style={{ marginBottom: "1.5rem" }}>
                <Label htmlFor="use_case_description" style={{ display: "block", marginBottom: 6, fontSize: "0.8rem", fontWeight: 600, color: "#444", textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "'Instrument Sans', system-ui, sans-serif" }}>
                  Describe Your Use Case *
                </Label>
                <Textarea id="use_case_description" required rows={4}
                  value={formData.use_case_description}
                  onChange={e => setFormData({ ...formData, use_case_description: e.target.value })}
                  placeholder="How will you use the MCP endpoint? What problem does it solve for your users?"
                  style={{ border: `1px solid #ddd`, borderRadius: 0, resize: "vertical", fontFamily: "'Instrument Sans', system-ui, sans-serif", fontSize: "0.875rem" }}
                />
              </div>

              <div style={{ display: "flex", gap: 12 }}>
                <button type="submit" disabled={isSubmitting} style={{
                  flex: 1, background: isSubmitting ? "#aaa" : RED, color: "white", border: "none",
                  cursor: isSubmitting ? "not-allowed" : "pointer", padding: "0.9rem",
                  fontSize: "0.9rem", fontWeight: 700, letterSpacing: "0.08em",
                  textTransform: "uppercase", fontFamily: "'Instrument Sans', system-ui, sans-serif",
                }}>
                  {isSubmitting ? "Submitting..." : "Submit Application"}
                </button>
                <button type="button" onClick={() => setShowForm(false)} disabled={isSubmitting} style={{
                  background: "transparent", color: NAVY, border: `2px solid ${NAVY}`,
                  cursor: "pointer", padding: "0.9rem 1.5rem", fontSize: "0.9rem",
                  fontWeight: 600, fontFamily: "'Instrument Sans', system-ui, sans-serif",
                }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ── FOOTER TAGLINE ─────────────────────────────────────────── */}
        <div style={{ textAlign: "center", padding: "2rem 0 0.5rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "center", marginBottom: "0.5rem" }}>
            <div style={{ height: 1, width: 40, background: `${GOLD}66` }} />
            <Star size={12} fill={GOLD} stroke="none" />
            <div style={{ height: 1, width: 40, background: `${GOLD}66` }} />
          </div>
          <p style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif", fontSize: "0.75rem", color: "#aaa" }}>
            {brand.name} · Open Data for Every American · MCP Protocol 2025
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Helper sub-components ──────────────────────────────────────────────────

function SectionHeader({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: "1rem" }}>
      {icon}
      <h2 style={{ fontSize: "1.05rem", fontWeight: 800, color: NAVY, fontFamily: "'Instrument Sans', system-ui, sans-serif", textTransform: "uppercase", letterSpacing: "0.1em", margin: 0 }}>
        {title}
      </h2>
      <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${NAVY}33, transparent)` }} />
    </div>
  );
}

function FormField({ label, id, type = "text", required = false, value, onChange }: {
  label: string; id: string; type?: string; required?: boolean;
  value: string; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <div>
      <Label htmlFor={id} style={{ display: "block", marginBottom: 6, fontSize: "0.8rem", fontWeight: 600, color: "#444", textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "'Instrument Sans', system-ui, sans-serif" }}>
        {label}
      </Label>
      <Input id={id} type={type} required={required} value={value} onChange={onChange}
        style={{ border: `1px solid #ddd`, borderRadius: 0, height: 40, fontFamily: "'Instrument Sans', system-ui, sans-serif", fontSize: "0.875rem" }}
      />
    </div>
  );
}
